import { getAntiforgeryToken, setAntiforgeryToken } from "../api/http";

// Ingreso y segundo factor contra el servidor del portal nuevo (/api/portal/session). El servidor valida, habla con
// Identity y emite la cookie; aquí solo se envía el formulario y se lee el paso siguiente.

export type FieldIssue = { field: string; messageKey: string };
export type LoginResult = {
  status: "signedIn" | "mfa" | "changePassword" | "invalid" | "error";
  next: string | null;
  code: string | null;
  messageKey: string | null;
  retryAfterSeconds: number | null;
  fields: FieldIssue[] | null;
};

/** Token antiforgery de la sesión actual (lo pide antes del primer envío). */
async function antiforgery(): Promise<string> {
  const current = getAntiforgeryToken();
  if (current) return current;
  const session = (await (await fetch("/api/portal/session", { credentials: "same-origin" })).json()) as {
    antiforgeryToken: string;
  };
  setAntiforgeryToken(session.antiforgeryToken);
  return session.antiforgeryToken;
}

/** POST que devuelve el resultado también con 4xx/5xx (el servidor siempre responde LoginResult). */
async function post(path: string, body: unknown): Promise<LoginResult> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-TOKEN": await antiforgery() },
    body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => null)) as LoginResult | null;
  return (
    result ?? {
      status: "error",
      next: null,
      code: "TECHNICAL_FAILURE",
      messageKey: null,
      retryAfterSeconds: null,
      fields: null,
    }
  );
}

export const signIn = (body: {
  email: string;
  password: string;
  keepSignedIn: boolean;
  returnUrl: string | null;
  returnOrg: number | null;
}) => post("/api/portal/session/login", body);

export const completeMfa = (body: { token: string | null; otp: string }) => post("/api/portal/session/mfa", body);

/**
 * Espera real en segundos o minutos redondeados hacia arriba (LoginFeedback.FormatMessage del portal actual): solo si
 * el texto tiene {time}.
 */
export function formatWait(template: string, seconds: number | null, label: (key: string) => string): string {
  if (!seconds || seconds <= 0 || !template.includes("{time}")) return template;
  const minutes = seconds >= 60;
  const count = minutes ? Math.ceil(seconds / 60) : seconds;
  const unit = label(
    minutes ? (count === 1 ? "LOGIN_MINUTE" : "LOGIN_MINUTES") : count === 1 ? "LOGIN_SECOND" : "LOGIN_SECONDS",
  );
  return template.replace("{time}", `${count} ${unit}`);
}
